import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { colors, mobile } from '../../styles';
import { parseVenueText } from './format';

// Long text: soft fade + "Read more" (venue editing ledger, V3).
const LINES_DESKTOP = 12;
const LINES_PHONE = 8;
const MIN_HIDDEN_LINES = 3; // Do not hide less than this. One hidden line behind a button looks silly.
const OPEN_MS = 300;

const Wrapper = styled.div`
  overflow: hidden;
  transition: max-height ${OPEN_MS}ms ease-out;

  &:focus {
    outline: none;
  }

  p {
    margin: 0 0 1em;
  }

  ul {
    margin: 0 0 1em;
    padding-left: 1.25em;
    list-style: disc;
  }

  & > :last-child {
    margin-bottom: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const ReadMore = styled.button`
  display: block;
  padding: 8px 0;
  border: none;
  background: none;
  color: ${colors.defaultText};
  font: inherit;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
`;

const renderInline = (inline) =>
  inline.map(({ text, bold, italic }, i) => {
    let node = text;
    if (italic) node = <em>{node}</em>;
    if (bold) node = <strong>{node}</strong>;
    return <span key={i}>{node}</span>;
  });

const renderBlock = (block, i) =>
  block.type === 'ul' ? (
    <ul key={i}>
      {block.items.map((item, j) => (
        <li key={j}>{renderInline(item)}</li>
      ))}
    </ul>
  ) : (
    <p key={i}>
      {block.lines.map((line, j) => (
        <span key={j}>
          {j > 0 && <br />}
          {renderInline(line)}
        </span>
      ))}
    </p>
  );

const lineHeightPx = (el) => {
  const style = window.getComputedStyle(el);
  return parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.2 || 20;
};

const VenueText = ({ text }) => {
  const blocks = useMemo(() => parseVenueText(text), [text]);
  const wrapperRef = useRef(null);
  const contentRef = useRef(null);
  const [clampPx, setClampPx] = useState(null); // null = short text, no clamp
  // closed → opening (max-height grows to the full height) → open (no limit, so the text can reflow)
  const [openPx, setOpenPx] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
    setOpenPx(null);
  }, [text]);

  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content || open) return undefined;
    const measure = () => {
      const lineHeight = lineHeightPx(content);
      const lines = window.matchMedia?.(`(max-width: ${mobile})`).matches ? LINES_PHONE : LINES_DESKTOP;
      const tooTall = content.scrollHeight > (lines + MIN_HIDDEN_LINES) * lineHeight;
      setClampPx(tooTall ? Math.round(lines * lineHeight) : null);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    return () => observer.disconnect();
  }, [blocks, open]);

  useEffect(() => {
    if (openPx === null) return undefined;
    const timer = window.setTimeout(() => setOpenPx(null), OPEN_MS + 50);
    return () => window.clearTimeout(timer);
  }, [openPx]);

  if (!blocks.length) return null;

  const clamped = clampPx !== null && !open;

  const onReadMore = () => {
    setOpenPx(contentRef.current.scrollHeight);
    setOpen(true);
    wrapperRef.current.focus({ preventScroll: true }); // The button goes away; keep keyboard focus here.
  };

  let style;
  if (clamped) {
    const fade = `linear-gradient(to bottom, #000 calc(100% - ${Math.round(clampPx * 0.25)}px), transparent)`;
    style = { maxHeight: `${clampPx}px`, WebkitMaskImage: fade, maskImage: fade };
  } else if (openPx !== null) {
    style = { maxHeight: `${openPx}px` };
  }

  return (
    <>
      <Wrapper ref={wrapperRef} tabIndex={-1} style={style}>
        <div ref={contentRef}>{blocks.map(renderBlock)}</div>
      </Wrapper>
      {clamped && (
        <ReadMore type="button" aria-expanded="false" onClick={onReadMore}>
          Read more
        </ReadMore>
      )}
    </>
  );
};

export default VenueText;
